import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { collection, getDocs, doc, updateDoc, setDoc, Timestamp, query, where } from "firebase/firestore";
import { db } from "../firebase";
import useBoostSettings, { DEFAULT_BOOST } from "../hooks/useBoostSettings";
import { isFeatured } from "../hooks/useVendors";
import { useLang } from "../i18n/LanguageContext";

const fmt = (ts) =>
  ts && ts.seconds ? new Date(ts.seconds * 1000).toLocaleDateString() : "—";

export default function BoostAdmin() {
  const { t } = useLang();
  const { settings: loaded, loaded: settingsLoaded } = useBoostSettings();
  const [pricing, setPricing] = useState(null);
  const [requests, setRequests] = useState([]);
  const [spots, setSpots] = useState(0);
  const [savedMsg, setSavedMsg] = useState(false);

  useEffect(() => {
    if (settingsLoaded && !pricing) setPricing(loaded);
  }, [settingsLoaded, loaded, pricing]);

  const load = async () => {
    const snap = await getDocs(collection(db, "boostRequests"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const order = { requested: 0, approved: 1, active: 2, declined: 3, expired: 4 };
    list.sort((a, b) => {
      const oa = order[a.status] ?? 9;
      const ob = order[b.status] ?? 9;
      if (oa !== ob) return oa - ob;
      return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    });
    setRequests(list);
    const fsnap = await getDocs(
      query(collection(db, "vendors"), where("featured", "==", true), where("isActive", "==", true))
    );
    setSpots(fsnap.docs.map((d) => d.data()).filter(isFeatured).length);
  };

  useEffect(() => {
    load();
  }, []);

  const approve = async (r) => {
    await updateDoc(doc(db, "boostRequests", r.id), { status: "approved" });
    load();
  };
  const decline = async (r) => {
    await updateDoc(doc(db, "boostRequests", r.id), { status: "declined" });
    load();
  };
  const confirmPaid = async (r) => {
    const end = new Date();
    end.setDate(end.getDate() + Number(r.days));
    await updateDoc(doc(db, "vendors", r.vendorId), {
      featured: true,
      featuredUntil: Timestamp.fromDate(end),
      featuredDays: Number(r.days),
      featuredAmount: Number(r.price),
    });
    await updateDoc(doc(db, "boostRequests", r.id), {
      status: "active",
      activatedAt: Timestamp.fromDate(new Date()),
      expiresAt: Timestamp.fromDate(end),
    });
    load();
  };
  const removeBoost = async (r) => {
    await updateDoc(doc(db, "vendors", r.vendorId), { featured: false });
    await updateDoc(doc(db, "boostRequests", r.id), { status: "expired" });
    load();
  };

  const savePricing = async () => {
    await setDoc(doc(db, "settings", "boost"), {
      pricePerDay: Number(pricing.pricePerDay) || 0,
      maxSlots: Number(pricing.maxSlots) || 6,
      freeBoostDays: Number(pricing.freeBoostDays) || 0,
      bundles: (pricing.bundles || [])
        .map((b) => ({ days: Number(b.days) || 0, price: Number(b.price) || 0 }))
        .filter((b) => b.days > 0),
    });
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2500);
  };

  const setP = (k, v) => setPricing((p) => ({ ...p, [k]: v }));
  const setBundle = (i, k, v) => setPricing((p) => ({ ...p, bundles: p.bundles.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const addBundle = () => setPricing((p) => ({ ...p, bundles: [...(p.bundles || []), { days: 7, price: 15 }] }));
  const removeBundle = (i) => setPricing((p) => ({ ...p, bundles: p.bundles.filter((_, j) => j !== i) }));

  if (!pricing) return <Empty>…</Empty>;
  const maxSlots = Number(pricing.maxSlots) || DEFAULT_BOOST.maxSlots;

  return (
    <>
      <Scarcity $full={spots >= maxSlots}>
        {spots}/{maxSlots} {t("admin.spotsFilled")}
      </Scarcity>

      <Panel>
        <h3>{t("admin.pricing")}</h3>
        <PriceRow>
          <Group>
            <label>{t("admin.pricePerDay")}</label>
            <input type="number" min="0" value={pricing.pricePerDay} onChange={(e) => setP("pricePerDay", e.target.value)} />
          </Group>
          <Group>
            <label>{t("admin.maxSlots")}</label>
            <input type="number" min="1" value={pricing.maxSlots} onChange={(e) => setP("maxSlots", e.target.value)} />
          </Group>
          <Group>
            <label>{t("admin.freeBoostDays")}</label>
            <input type="number" min="0" value={pricing.freeBoostDays != null ? pricing.freeBoostDays : 3} onChange={(e) => setP("freeBoostDays", e.target.value)} />
          </Group>
        </PriceRow>

        <BundlesLabel>{t("admin.bundles")}</BundlesLabel>
        {(pricing.bundles || []).map((b, i) => (
          <BundleRow key={i}>
            <input type="number" min="1" value={b.days} onChange={(e) => setBundle(i, "days", e.target.value)} />
            <span>{t("boost.days")} = €</span>
            <input type="number" min="0" value={b.price} onChange={(e) => setBundle(i, "price", e.target.value)} />
            <button className="rm" onClick={() => removeBundle(i)}>×</button>
          </BundleRow>
        ))}
        <AddBundle onClick={addBundle}>+ {t("admin.addBundle")}</AddBundle>

        <SaveRow>
          <button onClick={savePricing}>{t("admin.save")}</button>
          {savedMsg && <span>{t("manage.saved")}</span>}
        </SaveRow>
      </Panel>

      <h3 style={{ margin: "8px 0 4px", fontFamily: "inherit" }}>{t("admin.boostRequests")}</h3>
      {requests.length === 0 ? (
        <Empty>{t("admin.noRequests")}</Empty>
      ) : (
        <List>
          {requests.map((r) => (
            <ReqRow key={r.id}>
              <ReqInfo>
                <strong>{r.vendorName}{r.free && <Free>{t("admin.free")}</Free>}</strong>
                <span>{r.days} {t("boost.days")} · {r.free ? t("admin.free") : `€${r.price}`} · {fmt(r.createdAt)}</span>
                <StatusBadge $s={r.status}>{t(`admin.bs_${r.status}`)}</StatusBadge>
              </ReqInfo>
              <ReqActions>
                {r.status === "requested" && (
                  <>
                    {r.free ? (
                      <button className="go" onClick={() => confirmPaid(r)}>{t("admin.activate")}</button>
                    ) : (
                      <button className="go" onClick={() => approve(r)}>{t("admin.approve")}</button>
                    )}
                    <button className="del" onClick={() => decline(r)}>{t("admin.decline")}</button>
                  </>
                )}
                {r.status === "approved" && (
                  <>
                    <button className="go" onClick={() => confirmPaid(r)}>{t("admin.confirmPaid")}</button>
                    <button className="del" onClick={() => decline(r)}>{t("admin.decline")}</button>
                  </>
                )}
                {r.status === "active" && (
                  <>
                    <span className="exp">{t("admin.ends")} {fmt(r.expiresAt)}</span>
                    <button className="del" onClick={() => removeBoost(r)}>{t("admin.removeBoost")}</button>
                  </>
                )}
              </ReqActions>
            </ReqRow>
          ))}
        </List>
      )}
    </>
  );
}

const Free = styled.span`
  margin-left: 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.greenDeep};
  background: ${({ theme }) => theme.colors.gold};
  padding: 2px 8px;
  border-radius: 100px;
  vertical-align: middle;
`;

const Scarcity = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $full, theme }) => ($full ? theme.colors.rose : theme.colors.green)};
  margin-bottom: 16px;
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.hair};
  border-radius: 14px;
  padding: 18px;
  margin-bottom: 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  h3 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: 18px; }
`;

const PriceRow = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  label { font-size: 12px; color: ${({ theme }) => theme.colors.inkSoft}; }
  input { width: 110px; font-family: inherit; font-size: 14px; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 10px; padding: 9px 12px; background: ${({ theme }) => theme.colors.cream}; outline: none; }
`;

const BundlesLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.inkSoft};
  margin-top: 4px;
`;

const BundleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  span { font-size: 13px; color: ${({ theme }) => theme.colors.inkSoft}; }
  input { width: 80px; font-family: inherit; font-size: 14px; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 10px; padding: 8px 10px; background: ${({ theme }) => theme.colors.cream}; outline: none; }
  .rm { background: transparent; border: none; color: ${({ theme }) => theme.colors.rose}; font-size: 18px; cursor: pointer; }
`;

const AddBundle = styled.button`
  align-self: flex-start;
  background: transparent;
  border: 1px dashed ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.green};
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;
`;

const SaveRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  button { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; border: none; border-radius: 100px; padding: 10px 22px; font-size: 14px; font-weight: 500; cursor: pointer; }
  span { color: ${({ theme }) => theme.colors.green}; font-size: 13px; font-weight: 500; }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReqRow = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.hair};
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
`;

const ReqInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  strong { font-size: 15px; }
  span { font-size: 13px; color: ${({ theme }) => theme.colors.inkSoft}; }
`;

const StatusBadge = styled.span`
  align-self: flex-start;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 100px;
  color: ${({ $s, theme }) =>
    $s === "active" ? theme.colors.greenDeep : $s === "declined" || $s === "expired" ? theme.colors.rose : "#9a7b1f"};
  background: ${({ $s, theme }) =>
    $s === "active" ? theme.colors.gold : $s === "declined" || $s === "expired" ? "rgba(217,140,130,.15)" : "rgba(201,182,142,.22)"};
`;

const ReqActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  .exp { font-size: 12px; color: ${({ theme }) => theme.colors.inkSoft}; }
  button { border: none; border-radius: 10px; padding: 9px 16px; font-size: 13px; font-weight: 500; cursor: pointer; }
  .go { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; }
  .del { background: transparent; color: ${({ theme }) => theme.colors.rose}; }
`;

const Empty = styled.div`
  padding: 30px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.inkSoft};
`;
